import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Calendar, ArrowLeft, Save, X, Image, 
  MapPin, Clock, Tag, Ticket, Plus,
  DollarSign, Users, Info, CheckCircle,
  Link as LinkIcon, Building, FolderOpen
} from 'lucide-react';
import { createEvent, getGalleries } from '../utils/eventsAdminApi';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import { supabase } from '../utils/supabaseClient';
import RichTextEditor from './ui/RichTextEditor';
import '../styles/EventCreatePage.css';

const EventCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [linkedCollection, setLinkedCollection] = useState(null);

  // Parse collection_id from URL query params (e.g., /events/new?collection_id=123)
  const searchParams = new URLSearchParams(location.search);
  const collectionIdFromUrl = searchParams.get('collection_id');
  const [gallerySearch, setGallerySearch] = useState('');
  const [showGalleryDropdown, setShowGalleryDropdown] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);

  // Event form state
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    event_start: '',
    event_end: '',
    gallery_id: '',
    event_type: 'show',
    cover_image: '',
    capacity: '',
    external_url: '',
    eventbrite_id: '',
    collection_id: collectionIdFromUrl ? parseInt(collectionIdFromUrl) : null
  });

  // Ticket types for the event
  const [ticketTypes, setTicketTypes] = useState([]);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState({
    ticket_type_name: '',
    description: '',
    regular_price: '',
    early_bird_price: '',
    early_bird_deadline: '',
    tickets_available: '',
    sale_start_date: '',
    sale_end_date: ''
  });

  useEffect(() => {
    loadGalleries();
    // Load linked collection details if collection_id provided
    if (collectionIdFromUrl) {
      loadLinkedCollection(collectionIdFromUrl);
    }
  }, []);

  const loadLinkedCollection = async (collectionId) => {
    try {
      const { data, error } = await supabase
        .from('collections')
        .select('collection_id, title, cover_image')
        .eq('collection_id', collectionId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error loading collection:', error);
        return;
      }
      
      if (data) {
        setLinkedCollection(data);
      }
    } catch (err) {
      console.error('Error loading linked collection:', err);
    }
  };

  const loadGalleries = async () => {
    try {
      const data = await getGalleries();
      setGalleries(data);
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter galleries based on search
  const filteredGalleries = galleries?.filter(gallery =>
    (gallery.name?.toLowerCase() || '').includes(gallerySearch.toLowerCase()) ||
    (gallery.address?.toLowerCase() || '').includes(gallerySearch.toLowerCase())
  ) || [];

  const handleGallerySelect = (gallery) => {
    setEventForm({ ...eventForm, gallery_id: gallery.gallery_id });
    setSelectedGallery(gallery);
    setGallerySearch(`${gallery.name || ''}`);
    setShowGalleryDropdown(false);
  };

  const handleGallerySearchChange = (e) => {
    const value = e.target.value;
    setGallerySearch(value);
    setShowGalleryDropdown(true);
    
    if (value !== selectedGallery?.name) {
      setEventForm({ ...eventForm, gallery_id: '' });
      setSelectedGallery(null);
    }
  };

  const clearGallerySelection = () => {
    setGallerySearch('');
    setEventForm({ ...eventForm, gallery_id: '' });
    setSelectedGallery(null);
    setShowGalleryDropdown(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await uploadToCloudinary(file, 'image', 'events/covers');
      setEventForm(prev => ({
        ...prev,
        cover_image: result.url
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setEventForm({ ...eventForm, cover_image: '' });
  };

  const addTicketType = () => {
    if (!newTicket.ticket_type_name || !newTicket.regular_price) {
      alert('Please enter ticket name and price');
      return;
    }

    setTicketTypes([...ticketTypes, { ...newTicket, id: Date.now() }]);
    setNewTicket({
      ticket_type_name: '',
      description: '',
      regular_price: '',
      early_bird_price: '',
      early_bird_deadline: '',
      tickets_available: '',
      sale_start_date: '',
      sale_end_date: ''
    });
    setShowTicketForm(false);
  };

  const removeTicketType = (id) => {
    setTicketTypes(ticketTypes.filter(t => t.id !== id));
  };

  const handleSubmit = async () => {
    if (!eventForm.name || !eventForm.event_start || !eventForm.gallery_id) {
      alert('Please fill in all required fields (Name, Start Date, and Gallery)');
      return;
    }

    setCreating(true);
    try {
      // Create the event
      const newEvent = await createEvent(eventForm);
      
      // TODO: Create ticket types for the event if any were added
      // This would require an API call to create each ticket type
      
      // Navigate to the event detail page for further configuration
      navigate(`/curator/events/${newEvent.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const steps = [
    { id: 1, label: 'Basic Info', icon: Info },
    { id: 2, label: 'Date & Location', icon: MapPin },
    { id: 3, label: 'Tickets', icon: Ticket },
    { id: 4, label: 'Review', icon: CheckCircle }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return eventForm.name && eventForm.event_type;
      case 2:
        return eventForm.event_start && eventForm.gallery_id;
      case 3:
        return true; // Tickets are optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.searchable-select')) {
        setShowGalleryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="event-create-page">
        <div className="event-create-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-create-page">
      {/* Header */}
      <div className="event-create-header">
        <button 
          onClick={() => navigate('/curator/events')} 
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Events
        </button>
        <div className="header-title">
          <Calendar className="header-icon" />
          <h1>Create New Event</h1>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => navigate('/curator/events')} 
            className="cancel-btn"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="save-btn"
            disabled={creating || !canProceed()}
          >
            {creating ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="steps-container">
        <div className="steps-progress">
          {steps.map((step, index) => (
            <div 
              key={step.id}
              className={`step ${currentStep >= step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
              onClick={() => setCurrentStep(step.id)}
            >
              <div className="step-indicator">
                <step.icon size={18} />
              </div>
              <span className="step-label">{step.label}</span>
              {index < steps.length - 1 && <div className="step-connector" />}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="event-create-content">
        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Basic Information</h2>
              <p>Enter the essential details about your event</p>
            </div>

            {/* Linked Collection Notice */}
            {linkedCollection && (
              <div className="linked-collection-notice">
                <FolderOpen size={18} />
                <div className="linked-collection-info">
                  <span className="linked-label">Creating event for collection:</span>
                  <strong>{linkedCollection.title}</strong>
                </div>
                <button 
                  className="unlink-btn"
                  onClick={() => {
                    setLinkedCollection(null);
                    setEventForm({ ...eventForm, collection_id: null });
                  }}
                  title="Create event without linking to collection"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <div className="form-section">
              <div className="form-group full-width">
                <label>Event Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                  placeholder="Enter a descriptive event name..."
                  className="large-input"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <RichTextEditor
                  value={eventForm.description}
                  onChange={(content) => setEventForm({ ...eventForm, description: content })}
                  placeholder="Describe what attendees can expect at this event..."
                  minHeight="150px"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Event Type <span className="required">*</span></label>
                  <select
                    value={eventForm.event_type}
                    onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                  >
                    <option value="show">Art Show</option>
                    <option value="exhibition">Exhibition</option>
                    <option value="opening">Opening Reception</option>
                    <option value="workshop">Workshop</option>
                    <option value="artist_talk">Artist Talk</option>
                    <option value="auction">Auction</option>
                    <option value="festival">Festival</option>
                    <option value="fair">Art Fair</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Capacity</label>
                  <input
                    type="number"
                    value={eventForm.capacity}
                    onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                    placeholder="Max attendees"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Cover Image</label>
                <div className="image-upload-area">
                  {!eventForm.cover_image ? (
                    <div className="upload-placeholder">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        id="cover-image-upload"
                        hidden
                      />
                      <label htmlFor="cover-image-upload" className="upload-label">
                        {uploadingImage ? (
                          <>
                            <div className="spinner small"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Image size={32} />
                            <span>Click to upload cover image</span>
                            <small>Recommended: 1200x630px</small>
                          </>
                        )}
                      </label>
                    </div>
                  ) : (
                    <div className="image-preview">
                      <img src={eventForm.cover_image} alt="Event cover" />
                      <button onClick={removeImage} className="remove-image">
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Date & Location */}
        {currentStep === 2 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Date & Location</h2>
              <p>When and where will your event take place?</p>
            </div>

            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date & Time <span className="required">*</span></label>
                  <input
                    type="datetime-local"
                    value={eventForm.event_start}
                    onChange={(e) => setEventForm({ ...eventForm, event_start: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={eventForm.event_end}
                    onChange={(e) => setEventForm({ ...eventForm, event_end: e.target.value })}
                    min={eventForm.event_start}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Venue / Gallery <span className="required">*</span></label>
                <div className="searchable-select">
                  <div className="input-with-icon">
                    <Building size={18} className="input-icon" />
                    <input
                      type="text"
                      value={gallerySearch}
                      onChange={handleGallerySearchChange}
                      onFocus={() => setShowGalleryDropdown(true)}
                      placeholder="Search for a gallery or venue..."
                      autoComplete="off"
                    />
                    {gallerySearch && (
                      <button
                        type="button"
                        className="clear-button"
                        onClick={clearGallerySelection}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  {showGalleryDropdown && (
                    <div className="dropdown-list">
                      {filteredGalleries.length > 0 ? (
                        filteredGalleries.slice(0, 10).map((gallery) => (
                          <div
                            key={gallery.gallery_id}
                            className={`dropdown-item ${selectedGallery?.gallery_id === gallery.gallery_id ? 'selected' : ''}`}
                            onClick={() => handleGallerySelect(gallery)}
                          >
                            <div className="gallery-name">{gallery.name || 'Unnamed Gallery'}</div>
                            <div className="gallery-address">
                              <MapPin size={12} />
                              {gallery.address || 'No address'}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item no-results">
                          No galleries found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedGallery && (
                  <div className="selected-gallery-info">
                    <MapPin size={14} />
                    <span>{selectedGallery.address}</span>
                  </div>
                )}
              </div>

              <div className="form-group full-width">
                <label>External Event Link</label>
                <div className="input-with-icon">
                  <LinkIcon size={18} className="input-icon" />
                  <input
                    type="url"
                    value={eventForm.external_url}
                    onChange={(e) => setEventForm({ ...eventForm, external_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <small className="form-hint">Link to Eventbrite, Facebook Event, or your website</small>
              </div>

              <div className="form-group full-width">
                <label>Eventbrite Event ID</label>
                <div className="input-with-icon">
                  <Tag size={18} className="input-icon" />
                  <input
                    type="text"
                    value={eventForm.eventbrite_id}
                    onChange={(e) => setEventForm({ ...eventForm, eventbrite_id: e.target.value })}
                    placeholder="e.g., 123456789"
                  />
                </div>
                <small className="form-hint">For Eventbrite integration and attendee sync</small>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Tickets */}
        {currentStep === 3 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Ticket Types</h2>
              <p>Set up ticket options for your event (optional - can be configured later)</p>
            </div>

            <div className="form-section">
              {ticketTypes.length > 0 && (
                <div className="ticket-types-list">
                  {ticketTypes.map((ticket) => (
                    <div key={ticket.id} className="ticket-type-card">
                      <div className="ticket-info">
                        <h4>{ticket.ticket_type_name}</h4>
                        <p>{ticket.description || 'No description'}</p>
                        <div className="ticket-meta">
                          <span className="ticket-price">
                            <DollarSign size={14} />
                            ${ticket.regular_price}
                          </span>
                          {ticket.early_bird_price && (
                            <span className="early-bird">
                              Early Bird: ${ticket.early_bird_price}
                            </span>
                          )}
                          {ticket.tickets_available && (
                            <span className="availability">
                              <Users size={14} />
                              {ticket.tickets_available} available
                            </span>
                          )}
                        </div>
                      </div>
                      <button 
                        onClick={() => removeTicketType(ticket.id)}
                        className="remove-ticket"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showTicketForm ? (
                <div className="ticket-form-card">
                  <h4>New Ticket Type</h4>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ticket Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newTicket.ticket_type_name}
                        onChange={(e) => setNewTicket({ ...newTicket, ticket_type_name: e.target.value })}
                        placeholder="e.g., General Admission, VIP"
                      />
                    </div>
                    <div className="form-group">
                      <label>Regular Price <span className="required">*</span></label>
                      <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input
                          type="number"
                          value={newTicket.regular_price}
                          onChange={(e) => setNewTicket({ ...newTicket, regular_price: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                      placeholder="What's included with this ticket?"
                      rows={2}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Early Bird Price</label>
                      <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input
                          type="number"
                          value={newTicket.early_bird_price}
                          onChange={(e) => setNewTicket({ ...newTicket, early_bird_price: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Early Bird Deadline</label>
                      <input
                        type="datetime-local"
                        value={newTicket.early_bird_deadline}
                        onChange={(e) => setNewTicket({ ...newTicket, early_bird_deadline: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tickets Available</label>
                      <input
                        type="number"
                        value={newTicket.tickets_available}
                        onChange={(e) => setNewTicket({ ...newTicket, tickets_available: e.target.value })}
                        placeholder="Unlimited"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sale Start</label>
                      <input
                        type="datetime-local"
                        value={newTicket.sale_start_date}
                        onChange={(e) => setNewTicket({ ...newTicket, sale_start_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="ticket-form-actions">
                    <button 
                      onClick={() => setShowTicketForm(false)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={addTicketType}
                      className="add-btn"
                    >
                      Add Ticket Type
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={() => setShowTicketForm(true)}
                  className="add-ticket-btn"
                >
                  <Plus size={18} />
                  Add Ticket Type
                </button>
              )}

              <div className="tickets-info-box">
                <Info size={18} />
                <p>
                  You can skip this step and configure tickets later from the event detail page. 
                  Tickets integrate with Stripe for secure payment processing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="step-content">
            <div className="step-header">
              <h2>Review & Create</h2>
              <p>Review your event details before creating</p>
            </div>

            <div className="review-section">
              <div className="review-card">
                {eventForm.cover_image && (
                  <div className="review-cover">
                    <img src={eventForm.cover_image} alt="Event cover" />
                  </div>
                )}
                
                <div className="review-details">
                  <h3>{eventForm.name || 'Untitled Event'}</h3>
                  <span className="event-type-badge">{eventForm.event_type}</span>
                  
                  {eventForm.description && (
                    <div 
                      className="review-description"
                      dangerouslySetInnerHTML={{ __html: eventForm.description }}
                    />
                  )}

                  <div className="review-meta">
                    <div className="meta-item">
                      <Clock size={16} />
                      <div>
                        <strong>Date & Time</strong>
                        <span>{formatDateForDisplay(eventForm.event_start)}</span>
                        {eventForm.event_end && (
                          <span className="end-date">to {formatDateForDisplay(eventForm.event_end)}</span>
                        )}
                      </div>
                    </div>

                    <div className="meta-item">
                      <MapPin size={16} />
                      <div>
                        <strong>Venue</strong>
                        <span>{selectedGallery?.name || 'Not selected'}</span>
                        {selectedGallery?.address && (
                          <span className="address">{selectedGallery.address}</span>
                        )}
                      </div>
                    </div>

                    {eventForm.capacity && (
                      <div className="meta-item">
                        <Users size={16} />
                        <div>
                          <strong>Capacity</strong>
                          <span>{eventForm.capacity} attendees</span>
                        </div>
                      </div>
                    )}

                    {ticketTypes.length > 0 && (
                      <div className="meta-item">
                        <Ticket size={16} />
                        <div>
                          <strong>Tickets</strong>
                          <span>{ticketTypes.length} ticket type(s)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {ticketTypes.length > 0 && (
                <div className="review-tickets">
                  <h4>Ticket Types</h4>
                  <div className="tickets-summary">
                    {ticketTypes.map((ticket) => (
                      <div key={ticket.id} className="ticket-summary-item">
                        <span className="ticket-name">{ticket.ticket_type_name}</span>
                        <span className="ticket-price">${ticket.regular_price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="step-navigation">
          {currentStep > 1 && (
            <button 
              onClick={() => setCurrentStep(currentStep - 1)}
              className="nav-btn prev"
            >
              Previous
            </button>
          )}
          
          <div className="nav-spacer" />
          
          {currentStep < 4 ? (
            <button 
              onClick={() => setCurrentStep(currentStep + 1)}
              className="nav-btn next"
              disabled={!canProceed()}
            >
              Next
            </button>
          ) : (
            <button 
              onClick={handleSubmit}
              className="nav-btn create"
              disabled={creating || !eventForm.name || !eventForm.event_start || !eventForm.gallery_id}
            >
              {creating ? (
                <>
                  <div className="spinner small"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Event
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCreatePage;
